// Lib
import React from 'react';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash/fp';

// Utils
import { getDisplayName, getEmail } from '../../../common/users/userHelper';

// Components
import UserAvatar from './UserAvatar';
import UserPlanBadge from './UserPlanBadge';

// Constants
import { USER_AVATAR_SIZES } from '../../../common/users/userConstants';

// Styles
import './UserDetailsListEntry.scss';

/**
 * This component is built in such a way that emails that are too long will be cut off
 * to show only the domain.
 * Rather than using JavaScript to do this, we use a combination of splitting up the parts
 * of the email address and then CSS flexbox reverse wrapping with out of order parts
 * (the domain is added to the DOM first) and hidden overflows to give the desired result.
 *
 * If an email doesn't have two parts after splitting it on the @ symbol, we just treat it
 * like a normal email
 */
const UserEntryEmail = ({ email }) => {
    if (!email) return null;

    const emailParts = email.split('@');

    if (emailParts.length !== 2)
        return (
            <span className="UserEntryEmail domain" title={email}>
                {email}
            </span>
        );

    return (
        <span className="UserEntryEmail" title={email}>
            <span className="domain">{emailParts[1]}</span>
            <span>{emailParts[0]}@</span>
        </span>
    );
};

UserEntryEmail.propTypes = {
    email: PropTypes.string,
};

const UserDetailsListEntry = ({
    user,
    currentlyViewing,
    nameBadge,
    hideBadge,
    size = USER_AVATAR_SIZES.REGULAR_SMALL,
}) => {
    const name = getDisplayName(user);

    const hasName = !isEmpty(name);

    const email = getEmail(user);
    const displayName = name || email;

    return (
        <div className="UserSelectionInputSuggestionsUser">
            <UserAvatar user={user} activeIndicator={currentlyViewing} size={size} />
            <div className="user-details">
                <div className="user-name">
                    <span className="name">{displayName}</span>
                    {!hideBadge && <UserPlanBadge user={user} />}
                    {nameBadge}
                </div>
                {hasName && <UserEntryEmail email={email} />}
            </div>
        </div>
    );
};

UserDetailsListEntry.propTypes = {
    user: PropTypes.object,
    nameBadge: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
    currentlyViewing: PropTypes.bool,
    hideBadge: PropTypes.bool,
    size: PropTypes.number,
};

export default UserDetailsListEntry;
